import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  InlineGrid,
  TextField,
  Button,
  DataTable,
  Text,
  Box,
  SkeletonBodyText,
  EmptyState,
} from '@shopify/polaris';
import reportsApi from '@/services/reports';

const today = () => new Date().toISOString().split('T')[0];
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
};

function formatCurrency(dollars: number): string {
  return `$${Number(dollars).toFixed(2)}`;
}

interface StatCardProps {
  label: string;
  value: string;
}

function StatCard({ label, value }: StatCardProps) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {label}
        </Text>
        <Text as="p" variant="headingLg">
          {value}
        </Text>
      </BlockStack>
    </Card>
  );
}

export default function ClinicRevenue() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState(daysAgo(30));
  const [endDate, setEndDate] = useState(today());
  const [clinicId, setClinicId] = useState('');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['report', 'clinic-revenue', startDate, endDate, clinicId],
    queryFn: () =>
      reportsApi.getClinicRevenue({
        startDate,
        endDate,
        ...(clinicId ? { clinicId } : {}),
      }),
  });

  const [exporting, setExporting] = useState(false);
  const handleExport = async () => {
    setExporting(true);
    try {
      await reportsApi.downloadCsv('/reports/clinic-revenue', {
        startDate,
        endDate,
        ...(clinicId ? { clinicId } : {}),
      });
    } finally {
      setExporting(false);
    }
  };

  // API returns data as a flat array of clinic objects
  const clinics = Array.isArray(data?.data) ? data.data : [];

  const rows =
    clinics.map((item) => [
      item.clinicName,
      item.orderCount,
      formatCurrency(item.revenue),
      formatCurrency(item.avgValue),
      formatCurrency(item.outstandingBalance),
    ]);

  return (
    <Page
      title="Clinic Revenue"
      backAction={{ content: 'Reports', onAction: () => navigate('/reports') }}
    >
      <Card>
        <BlockStack gap="400">
          <InlineStack gap="400" blockAlign="end">
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={setStartDate}
              autoComplete="off"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={setEndDate}
              autoComplete="off"
            />
            <TextField
              label="Clinic ID (optional)"
              value={clinicId}
              onChange={setClinicId}
              autoComplete="off"
            />
            <Box paddingBlockStart="600">
              <InlineStack gap="200">
                <Button onClick={() => refetch()}>Refresh</Button>
                <Button onClick={handleExport} loading={exporting}>
                  Export CSV
                </Button>
              </InlineStack>
            </Box>
          </InlineStack>

          {isLoading ? (
            <SkeletonBodyText lines={8} />
          ) : isError ? (
            <EmptyState heading="Failed to load report" image="">
              <p>There was an error loading the Clinic Revenue report. Try refreshing.</p>
            </EmptyState>
          ) : !data ? (
            <EmptyState heading="No data" image="">
              <p>No clinic revenue data found for the selected date range.</p>
            </EmptyState>
          ) : (
            <BlockStack gap="400">
              <InlineGrid columns={2} gap="400">
                <StatCard label="Total Revenue" value={formatCurrency(clinics.reduce((s, c) => s + c.revenue, 0))} />
                <StatCard label="Total Clinics" value={String(clinics.length)} />
              </InlineGrid>

              <DataTable
                columnContentTypes={['text', 'numeric', 'text', 'text', 'text']}
                headings={['Clinic', 'Orders', 'Revenue', 'Avg Value', 'Outstanding']}
                rows={rows}
                footerContent={rows.length === 0 ? 'No records found' : undefined}
              />
            </BlockStack>
          )}
        </BlockStack>
      </Card>
    </Page>
  );
}
